import { MEDIA_FIT_OPTIONS } from '@cornerstone/shared';
import { useEffect, useState } from 'react';
import type { MediaFitMode } from '@cornerstone/shared';
import { Button } from './ui';
import { ModalShell } from './menu';
import { cn } from '../utils/cn';
import { FitModeRadio } from './FitModeRadio';

const OPTIONS = MEDIA_FIT_OPTIONS.map(option => ({ mode: option.value, title: option.label, description: option.description }));

export type MediaFitModalMedia = {
  name: string;
  width: number;
  height: number;
  previewUrl?: string | null;
};

export function hasResolutionMismatch(
  media: Pick<MediaFitModalMedia, 'width' | 'height'>,
  targetWidth: number,
  targetHeight: number,
): boolean {
  return media.width > 0 && media.height > 0 &&
    (media.width !== targetWidth || media.height !== targetHeight);
}

export function MediaFitConfirmModal({
  media,
  targetWidth,
  targetHeight,
  defaultFitMode,
  confirmLabel = '추가',
  onCancel,
  onConfirm,
}: {
  media: MediaFitModalMedia;
  targetWidth: number;
  targetHeight: number;
  defaultFitMode: MediaFitMode;
  confirmLabel?: string;
  onCancel: () => void;
  onConfirm: (fitMode: MediaFitMode) => void;
}) {
  const [selected, setSelected] = useState(defaultFitMode);
  useEffect(() => setSelected(defaultFitMode), [defaultFitMode]);
  const sourceAspect = media.width / media.height;
  const targetAspect = targetWidth / targetHeight;
  const aspectMismatch = Math.abs(sourceAspect / targetAspect - 1) > 0.01;

  return (
    <ModalShell
      title="미디어 표시 방식"
      onClose={onCancel}
      panelClassName="w-full max-w-[840px] max-h-[calc(100vh-4rem)] overflow-y-auto"
    >
      <div className="flex min-w-0 flex-col gap-4">
        <div>
          <div className="text-[15px] text-text font-medium truncate">{media.name}</div>
          <div className="mt-1 font-mono text-[12px] text-text-dim break-words">
            원본 {media.width}×{media.height} ({sourceAspect.toFixed(2)}:1) · 플레이리스트 해상도 {targetWidth}×{targetHeight} ({targetAspect.toFixed(2)}:1)
          </div>
          <div className="mt-1 text-[13px] text-amber-400">
            {aspectMismatch
              ? '표시 방식에 따라 여백, 크롭 또는 왜곡이 발생합니다.'
              : '원본 비율은 같지만 출력 시 크기가 조정됩니다.'}
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="aspect-video w-full md:w-[420px] flex-none bg-black overflow-hidden rounded-sm flex items-center justify-center">
            {media.previewUrl ? (
              <img src={media.previewUrl} alt="" className="w-full h-full"
                style={{ objectFit: selected === 'stretch' ? 'fill' : selected }} />
            ) : <span className="text-[11px] text-text-dimmer">미리보기</span>}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <FitModeRadio label="표시 방식" size="md" value={selected} onChange={(value) => setSelected(value as MediaFitMode)} />
            <div className="mt-2 font-mono text-[13px] text-text">
              {OPTIONS.find((option) => option.mode === selected)?.title}
              <span className="ml-2 rounded bg-lime px-1.5 py-0.5 text-[10px] font-semibold text-black">선택됨</span>
            </div>
            <p className="text-[12px] leading-4 text-text-dim">{OPTIONS.find((option) => option.mode === selected)?.description}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>취소</Button>
          <Button variant="primary" onClick={() => onConfirm(selected)}>{confirmLabel}</Button>
        </div>
      </div>
    </ModalShell>
  );
}
